// 🔒 AVISO-CHROME-2026-10-23 — aviso de Chrome viejo en las PC con Windows
// (reglas y números en lib/avisoChrome.js). Solo avisa: nunca bloquea el trabajo.
//   amarillo → barra arriba, "Recordar mañana" la esconde hasta el otro día.
//   rojo / windows → ventana al abrir; "Continuar" la esconde hasta cerrar el navegador.
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AlertTriangle, AlertCircle, Monitor } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { safeStorage } from '@/lib/safeStorage';
import { versionChromeWindows, esWindowsViejo, esWindowsViejoUA, nivelAviso, hoyLocal, rutaSinAviso, CHROME_MINIMO } from '@/lib/avisoChrome';

const KEY_MANANA = 'vexly_aviso_chrome_pospuesto';
const KEY_SESION = 'vexly_aviso_chrome_visto';

function vistoEnSesion() {
  try { return sessionStorage.getItem(KEY_SESION) === '1'; } catch { return false; }
}
function marcarSesion() {
  try { sessionStorage.setItem(KEY_SESION, '1'); } catch { /* sin almacenamiento: vuelve a salir, no rompe */ }
}

const PASOS = [
  { n: 1, titulo: 'Toca los 3 puntos ⋮', texto: 'Están arriba a la derecha de Chrome, al lado de la barra de direcciones.' },
  { n: 2, titulo: 'Ayuda → Acerca de Google Chrome', texto: 'Chrome busca la actualización y la descarga solo. Espera a que termine.' },
  { n: 3, titulo: 'Toca el botón para reiniciar Chrome', texto: 'Chrome se cierra y abre de nuevo con VexlyPOS. Si no aparece el botón, ya estás al día.' },
];

export default function AvisoChrome() {
  const [nivel, setNivel] = useState(null);
  const [version, setVersion] = useState(null);
  const [pasos, setPasos] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    let vivo = true;
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const v = versionChromeWindows(ua);
    const viejoUA = esWindowsViejoUA(ua);
    if (!v) return undefined;
    setVersion(v);
    const decidir = (windowsViejo) => {
      if (!vivo) return;
      const n = nivelAviso(v, windowsViejo || viejoUA);
      if (n === 'amarillo' && safeStorage.get(KEY_MANANA) === hoyLocal()) return;
      if ((n === 'rojo' || n === 'windows') && vistoEnSesion()) return;
      setNivel(n);
    };
    try {
      const uad = navigator.userAgentData;
      if (uad && typeof uad.getHighEntropyValues === 'function') {
        uad.getHighEntropyValues(['platformVersion'])
          .then((h) => decidir(esWindowsViejo(h && h.platformVersion)))
          .catch(() => decidir(false));
      } else {
        decidir(false);
      }
    } catch {
      decidir(false);
    }
    return () => { vivo = false; };
  }, []);

  const posponer = () => { safeStorage.set(KEY_MANANA, hoyLocal()); setNivel(null); };
  const continuar = () => { marcarSesion(); setNivel(null); };
  const verPasos = () => {
    if (nivel !== 'amarillo') { marcarSesion(); setNivel(null); } // la barra amarilla vuelve al cerrar los pasos
    setPasos(true);
  };

  if ((!nivel && !pasos) || rutaSinAviso(pathname)) return null;
  const rojo = nivel === 'rojo' || nivel === 'windows';
  const puedeActualizar = version < CHROME_MINIMO; // Windows 7/8 con Chrome < 109 todavía puede subir a la 109

  return (
    <>
      {nivel === 'amarillo' && !pasos && (
        <div
          role="status"
          data-testid="aviso-chrome-amarillo"
          className="fixed top-0 inset-x-0 z-40 flex flex-wrap items-center gap-3 px-4 py-3 border-b-2 border-amber-500 bg-amber-100 text-amber-950 shadow-lg"
        >
          <AlertTriangle className="h-6 w-6 shrink-0 text-amber-600" aria-hidden="true" />
          <div className="flex-1 min-w-[200px]">
            <p className="font-bold text-sm">Hay una actualización de Chrome pendiente</p>
            <p className="text-sm opacity-90">Este equipo tiene Chrome {version}. Actualízalo cuando no estés cobrando.</p>
          </div>
          <button type="button" onClick={verPasos} data-testid="aviso-chrome-como"
            className="min-h-[44px] px-4 rounded-lg bg-amber-500 text-amber-950 font-bold text-sm">
            Cómo actualizar
          </button>
          <button type="button" onClick={posponer} data-testid="aviso-chrome-manana"
            className="min-h-[44px] px-4 rounded-lg border border-amber-600/60 font-semibold text-sm">
            Recordar mañana
          </button>
        </div>
      )}

      <Dialog open={rojo} onOpenChange={(o) => { if (!o) continuar(); }}>
        <DialogContent hideCloseButton className="max-w-md bg-card border-2 border-destructive" data-testid="aviso-chrome-rojo">
          <div className="mx-auto w-16 h-16 rounded-full bg-red-500/15 flex items-center justify-center">
            {nivel === 'windows'
              ? <Monitor className="h-8 w-8 text-red-600" aria-hidden="true" />
              : <AlertCircle className="h-8 w-8 text-red-600" aria-hidden="true" />}
          </div>
          <DialogTitle className="text-center font-oswald text-2xl text-red-600">
            {nivel === 'windows' ? 'Esta computadora ya no recibe actualizaciones' : 'Tu navegador es muy viejo'}
          </DialogTitle>
          <DialogDescription className="text-center text-base text-foreground">
            {nivel === 'windows'
              ? (puedeActualizar
                ? `Este equipo tiene Windows 7 u 8 y Chrome ${version}. Actualiza Chrome a la ${CHROME_MINIMO}, la última para ese Windows. Después de eso ya no recibe más actualizaciones.`
                : `Este equipo tiene Windows 7 u 8. Google dejó de actualizar Chrome para esos Windows en la versión ${CHROME_MINIMO}. Reinstalar Chrome no lo arregla.`)
              : `Este equipo tiene Chrome ${version}. VexlyPOS necesita Chrome ${CHROME_MINIMO} o más nuevo. Algunas pantallas pueden verse mal o fallar.`}
          </DialogDescription>
          {nivel === 'windows' && (
            <div className="rounded-lg bg-muted p-4 text-sm text-foreground">
              <p className="font-bold mb-1">Qué hacer</p>
              <p>Pasar esta computadora a Windows 10 u 11, o usar otro equipo para el sistema. Mientras tanto VexlyPOS puede seguir aquí, pero Chrome ya no recibe arreglos de seguridad.</p>
            </div>
          )}
          <div className="flex flex-col gap-3">
            {(nivel === 'rojo' || puedeActualizar) && (
              <button type="button" onClick={verPasos} data-testid="aviso-chrome-rojo-como"
                className="min-h-[52px] rounded-lg bg-primary text-primary-foreground font-oswald text-lg tracking-wide">
                CÓMO ACTUALIZAR CHROME
              </button>
            )}
            <button type="button" onClick={continuar} data-testid="aviso-chrome-continuar"
              className="min-h-[48px] rounded-lg bg-muted text-foreground font-semibold">
              {nivel === 'windows' ? 'Entendido' : 'Continuar de todos modos'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={pasos} onOpenChange={(o) => { if (!o) setPasos(false); }}>
        <DialogContent hideCloseButton className="max-w-xl bg-card" data-testid="aviso-chrome-pasos">
          <DialogTitle className="font-oswald text-2xl">Cómo actualizar Chrome</DialogTitle>
          <DialogDescription className="flex items-start gap-2 rounded-lg border border-amber-500/60 bg-amber-100 text-amber-950 p-3 text-sm">
            <AlertTriangle className="h-5 w-5 shrink-0" aria-hidden="true" />
            <span>Hazlo cuando no estés cobrando: Chrome se cierra y se vuelve a abrir solo.</span>
          </DialogDescription>
          <ol className="flex flex-col gap-3">
            {PASOS.map((p) => (
              <li key={p.n} className="flex gap-4 items-start rounded-lg bg-muted p-4">
                <span className="shrink-0 w-11 h-11 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-oswald text-xl font-bold">{p.n}</span>
                <div>
                  <p className="font-bold text-foreground">{p.titulo}</p>
                  <p className="text-sm text-muted-foreground">{p.texto}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="flex justify-end">
            <button type="button" onClick={() => setPasos(false)} data-testid="aviso-chrome-entendido"
              className="min-h-[48px] px-6 rounded-lg bg-primary text-primary-foreground font-oswald text-lg tracking-wide">
              ENTENDIDO
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
